﻿import { ReactNode } from "react";

export interface IKeyValuePair<K, T> {
    key: K;
    value: T;
}

export class Dictionary<K, T> {
    private readonly _values: Array<IKeyValuePair<K, T>>;

    constructor() {
        this._values = [];
    }

    public get length(): number {
        return this._values.length;
    }

    public push(key: K, value: T): void {
        if (this._values.filter((v) => v.key === key).length > 0) {
            throw new Error(
                `An element the with the key:${key} already exists in the dictionary.`,
            );
        } else {
            const kvp: IKeyValuePair<K, T> = {
                key,
                value,
            };

            this._values.push(kvp);
        }
    }

    public get(key: K): T {
        const returnVal: T = this._values.find((v) => v.key === key).value;

        return returnVal;
    }

    public set(key: K, value: T): void {
        const index: number = this.getIndexOf(key);

        if (index !== undefined || index !== null) {
            this._values[index].value = value;
        } else {
            throw new Error(
                `An element the with the key:${key} was not found in the dictionary.`,
            );
        }
    }

    public remove(key: K): void {
        const index: number = this.getIndexOf(key);

        if (index !== undefined || index !== null) {
            this._values.splice(index, 1);
        } else {
            throw new Error(
                `An element the with the key:${key} was not found in the dictionary.`,
            );
        }
    }

    public getIndexOf(key: K): number {
        const returnVal: number = this._values.findIndex((v) => v.key === key);

        return returnVal;
    }

    public sort(
        compareFn: (a: IKeyValuePair<K, T>, b: IKeyValuePair<K, T>) => number,
    ): void {
        this._values.sort(compareFn);
    }

    public forEach(func: (key: K, value: T) => void): void {
        for (let i = 0; i < this._values.length; i++) {
            func(this._values[i].key, this._values[i].value);
        }
    }

    public map(func: (key: K, value: T) => ReactNode): ReactNode[] {
        const nodes: React.ReactNode[] = [];

        for (let i = 0; i < this._values.length; i++) {
            nodes.push(func(this._values[i].key, this._values[i].value));
        }

        return nodes;
    }

    public contains(key: K): boolean {
        return this._values.findIndex((v) => v.key === key) !== -1;
    }
}
